import { dealtGames } from './state.js';
import type { DealSeat } from './state.js';
import { taskKey } from './tasks.js';
import type { Chapter, GameState, ShowState, TaskState } from './types.js';

/**
 * The ship's charge.
 *
 * Since 2026-09-28 (Marek, after the test evening) the games and the charge are one step, live: the
 * charge is the share of this stop's games that are done. Every finished game fills it where the
 * whole room can see; nothing fills it by the clock, and the stop's games done is the ship charged.
 * It is 100 through the jump and at docking, and the operator's override still wins. The old rules
 * — a clock floor, a wobble, 2398 stalling at 99.7 % — went with the separate charge step.
 *
 * Guest taps and seeds still add to `playerContribution` (the reducer), which the phones' own
 * little meters read; the ship's charge does not.
 */
export const CHARGE = {
  MIN: 0,
  MAX: 100,
  /** Participation's own pool stops here. */
  CONTRIBUTION_CAP: 96,
  /** What each guest action adds to `playerContribution`. */
  AWARD: { taskComplete: 4, tap: 2, seed: 8 },
  /** A tap flood must not move the pool (06-CLOUDFLARE.md, "Rate-limit guest intents"). */
  TAP_COOLDOWN_MS: 500,
  TAP_MAX_PER_SEAT_PER_CHAPTER: 10,
} as const;

export type ChargeAward = keyof typeof CHARGE.AWARD;

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Elapsed time in the chapter with HOLD subtracted. The console's HOLD freezes the
 * countdown ("guests see turbulence"); on RESUME the chapter's end shifts by the held duration.
 */
export function effectiveElapsedMs(state: Pick<ShowState, 'hold' | 'chapterStartedAt'>, now: number): number {
  const until = state.hold.active && state.hold.since !== null ? state.hold.since : now;
  return Math.max(0, until - state.chapterStartedAt - state.hold.accumulatedMs);
}

export function elapsedPct(state: Pick<ShowState, 'hold' | 'chapterStartedAt' | 'chapterLengthMs'>, now: number): number {
  if (state.chapterLengthMs <= 0) return 0;
  return (effectiveElapsedMs(state, now) / state.chapterLengthMs) * 100;
}

/** Milliseconds left in the chapter, for the console countdown. Never negative. */
export function msRemaining(state: Pick<ShowState, 'hold' | 'chapterStartedAt' | 'chapterLengthMs'>, now: number): number {
  return Math.max(0, state.chapterLengthMs - effectiveElapsedMs(state, now));
}

/** What the charge reads of the room: the wire's view will do, so every screen agrees without shipping it. */
export type ChargeState = Pick<ShowState, 'chapter' | 'phase'> & {
  seats: readonly DealSeat[];
  tasks: Readonly<Record<string, Pick<TaskState, 'chapter' | 'index' | 'seat' | 'done' | 'updatedAt'>>>;
  game: Pick<GameState, 'round'>;
};

/** One game of this stop: whose it is and which of the era's games. */
export interface StopGame {
  seat: number;
  index: number;
}

/**
 * This stop's games: every game dealt this era (state.ts `dealtGames`: each guest's own, and the
 * stand-ins for posts nobody holds) and whose it is — `allDealtDone`'s games, with one exception. Worked out from who is aboard now, so a guest
 * checked out or voted out drops out at once and a guest added mid-evening comes in — once the games
 * are open, as soon as they start one of theirs: a latecomer who has not played yet never holds the
 * ship back (Marek, 2026-09-28: "the whole game mechanics should be changed automatically to a new
 * amount of players"). Before the games open, everyone aboard.
 */
export function stopGames(state: ChargeState, chapter: Chapter = state.chapter): StopGame[] {
  if (chapter < 1) return [];
  const round = state.game.round && state.game.round.chapter === chapter ? state.game.round : null;
  // The exception: a latecomer's game counts from the moment they start it, not before.
  return dealtGames(state, chapter).filter(
    ({ seat, index }) => !round || round.seats.includes(seat) || state.tasks[taskKey(chapter, index, seat)] !== undefined,
  );
}

export interface GamesProgress {
  done: number;
  total: number;
  /** Seats whose game is not done yet, for the captain. */
  waiting: number[];
}

export function gamesProgress(state: ChargeState, chapter: Chapter = state.chapter): GamesProgress {
  const games = stopGames(state, chapter);
  const waiting = games.filter((g) => state.tasks[taskKey(chapter, g.index, g.seat)]?.done !== true).map((g) => g.seat);
  return { done: games.length - waiting.length, total: games.length, waiting };
}

/** Every game of this stop is done: the ship is charged (and there was at least one to play). */
export function shipCharged(state: ChargeState): boolean {
  const p = gamesProgress(state);
  return p.total > 0 && p.done === p.total;
}

/** When the last of this stop's games was finished, or null while the ship is not charged. */
export function chargedAt(state: ChargeState): number | null {
  if (!shipCharged(state)) return null;
  let last = 0;
  for (const g of stopGames(state)) last = Math.max(last, state.tasks[taskKey(state.chapter, g.index, g.seat)]?.updatedAt ?? 0);
  return last;
}

/**
 * The displayed charge: the share of this stop's games done, 0–100. Derived on every read — never
 * trusted from the wire.
 */
export function computeCharge(state: ChargeState & Pick<ShowState, 'chargeOverride'>, _now?: number): number {
  if (state.chargeOverride !== null) return round1(clamp(state.chargeOverride, 0, CHARGE.MAX));
  // The drive lands as the jump fires, and the ship is home at docking.
  if (state.phase === 'jump' || state.phase === 'farewell') return CHARGE.MAX;
  const { done, total } = gamesProgress(state);
  return total === 0 ? CHARGE.MIN : round1((done / total) * 100);
}

/** Add a guest contribution to the room-wide pool, capped. */
export function addContribution(current: number, award: ChargeAward): number {
  return Math.min(CHARGE.CONTRIBUTION_CAP, current + CHARGE.AWARD[award]);
}

/**
 * Whether this seat's tap earns. Taps outside the cooldown or past the per-chapter budget still
 * register as presence, they just do not add.
 */
export function tapEarns(
  record: { count: number; lastAt: number } | undefined,
  now: number,
): boolean {
  if (!record) return true;
  if (record.count >= CHARGE.TAP_MAX_PER_SEAT_PER_CHAPTER) return false;
  return now - record.lastAt >= CHARGE.TAP_COOLDOWN_MS;
}
